import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Database, Filter, Download, RefreshCw, FileText, Eye,
  ExternalLink, ChevronDown, ChevronRight, AlertTriangle,
  X, Search, Copy, Play, BarChart2, Bookmark, BookmarkPlus, Trash2, Pencil, Check
} from 'lucide-react';
import { resolveFilterShape, getBodyKeys, buildFilters, supportsFilters } from '../lib/filterShape';

// ─── Fetch Engine ─────────────────────────────────────────────────────────────

// Sprint 2: builds a hints-shaped adapter from cfg columns (Sprint 1B backfill).
// Returning the same shape as the old PARTNER_HINTS entries means downstream
// consumers (dimension/metric pickers, filter UI) don't need to change.
// PARTNER_HINTS registry deleted; filterability now declared inline on dimensions.
function detectPartnerHints(cfg) {
  const shape = resolveFilterShape(cfg);
  const bodyKeys = getBodyKeys(shape);
  const dims = cfg?.dimensions || [];
  const metrics = cfg?.metrics || [];
  return {
    shape,
    bodyKeys,
    dimensions: dims,
    metrics,
    filters: dims.filter(d => d.filterable !== false),
    filterOperators: (cfg?.operator_vocabulary || []).map(o => o.wire),
  };
}

const DATE_PRESET_LABELS = {
  today: 'Today', yesterday: 'Yesterday', last3: 'Last 3 days', last7: 'Last 7 days',
  thisMonth: 'This Month', lastMonth: 'Last Month', custom: 'Custom dates',
};

function FetchEngineTab() {
  const [configs, setConfigs]         = useState([]);
  const [selectedId, setSelectedId]   = useState('');
  const [configReady, setConfigReady] = useState(false);
  const [dateFrom, setDateFrom]       = useState('');
  const [dateTo, setDateTo]           = useState('');
  const [datePreset, setDatePreset]   = useState('last7');
  const [customBody, setCustomBody]   = useState('{}');
  const [customBodyErr, setCustomBodyErr] = useState('');
  const [fetching, setFetching]       = useState(false);
  const [cancelToken, setCancelToken] = useState(null); // axios CancelToken source
  const [result, setResult]           = useState(null);
  const [error, setError]             = useState('');
  const [colVisibility, setColVisibility] = useState({});
  const [sortCol, setSortCol]         = useState('');
  const [sortDir, setSortDir]         = useState(1); // 1 asc, -1 desc
  const [filterText, setFilterText]   = useState('');
  const [page, setPage]               = useState(0);
  const [fetchedAt, setFetchedAt]     = useState(null);
  const [activeFilters, setActiveFilters] = useState([]); // [{key, value, operator}]
  const [savedPresets, setSavedPresets] = useState([]);
  const [showPresets, setShowPresets]   = useState(false);
  const [namingPreset, setNamingPreset] = useState(false);
  const [presetName, setPresetName]     = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [activePresetId, setActivePresetId] = useState(null);
  const [editingPresetId, setEditingPresetId] = useState(null);
  const [editName, setEditName] = useState('');
  const PER_PAGE = 100;

  // ── Date preset helpers ──────────────────────────────────────────────────────
  // IMPORTANT: Use local-timezone-safe formatter — toISOString() shifts to UTC
  // which causes wrong dates for users in non-UTC timezones (e.g. IST = UTC+5:30)
  function fmtDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  function applyPreset(preset) {
    const today = new Date();
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    setDatePreset(preset);
    if (preset === 'today') {
      setDateFrom(fmtDate(today)); setDateTo(fmtDate(today));
    } else if (preset === 'yesterday') {
      setDateFrom(fmtDate(yesterday)); setDateTo(fmtDate(yesterday));
    } else if (preset === 'last3') {
      const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 3);
      setDateFrom(fmtDate(d)); setDateTo(fmtDate(yesterday));
    } else if (preset === 'last7') {
      const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
      setDateFrom(fmtDate(d)); setDateTo(fmtDate(yesterday));
    } else if (preset === 'lastMonth') {
      // First day of last month, last day of last month
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last  = new Date(today.getFullYear(), today.getMonth(), 0); // day 0 = last day of prev month
      setDateFrom(fmtDate(first)); setDateTo(fmtDate(last));
    } else if (preset === 'thisMonth') {
      // 1st of current month → yesterday
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(fmtDate(first)); setDateTo(fmtDate(yesterday));
    }
    // 'custom' — leave date fields as-is, let user edit
  }

  useEffect(() => { applyPreset('last7'); }, []);

  useEffect(() => {
    axios.get('/api/partner-configs')
      .then(r => { setConfigs(r.data); })
      .catch(() => toast.error('Failed to load partner configs'));
  }, []);

  // Saved presets for the selected config
  useEffect(() => {
    if (!selectedId) { setSavedPresets([]); return; }
    axios.get(`/api/partner-configs/${selectedId}/presets`)
      .then(r => setSavedPresets(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSavedPresets([]));
  }, [selectedId]);

  const selectedCfg = configs.find(c => String(c.id) === selectedId) || null;
  const hints = detectPartnerHints(selectedCfg);

  function initBody(cfg) {
    const existing = cfg?.request_body;
    setCustomBody(existing && Object.keys(existing).length > 0
      ? JSON.stringify(existing, null, 2)
      : '{\n  \n}');
    setResult(null); setError(''); setFilterText(''); setPage(0);
    setSortCol(''); setSortDir(1); setFetchedAt(null); setActiveFilters([]);
  }

  function handleConfigChange(id) {
    setSelectedId(id);
    setConfigReady(false);
    setResult(null); setError('');
    setShowPresets(false); setNamingPreset(false); setPresetName('');
    setActivePresetId(null);
  }

  function handleGo() {
    if (!selectedId) return toast.error('Please select a partner config first');
    const cfg = configs.find(c => String(c.id) === selectedId);
    initBody(cfg);
    setConfigReady(true);
  }

  function validateBody(v) {
    setCustomBody(v);
    try { JSON.parse(v); setCustomBodyErr(''); }
    catch { setCustomBodyErr('Invalid JSON'); }
  }

  // ── Insert dimension — supports groupBy, split_by, and dimensions (PAS) ────────
  function insertDimension(key) {
    try {
      const parsed = JSON.parse(customBody);
      const groupKey = parsed.hasOwnProperty('dimensions') ? 'dimensions'
                     : parsed.hasOwnProperty('split_by')   ? 'split_by'
                     : parsed.hasOwnProperty('groupBy')    ? 'groupBy'
                     : hints.bodyKeys?.dimensions || 'groupBy';
      if (!Array.isArray(parsed[groupKey])) parsed[groupKey] = [];
      if (!parsed[groupKey].includes(key)) parsed[groupKey].push(key);
      setCustomBody(JSON.stringify(parsed, null, 2));
      setCustomBodyErr('');
      toast.success(`Added "${key}" to ${groupKey}`);
    } catch { toast('Fix the JSON error first before adding dimensions', { icon: 'ℹ️' }); }
  }

  // ── Insert metric — supports fields, data_fields, and metrics (PAS) ───────────
  function insertMetric(key) {
    try {
      const parsed = JSON.parse(customBody);
      const fieldKey = parsed.hasOwnProperty('metrics')      ? 'metrics'
                     : parsed.hasOwnProperty('data_fields')  ? 'data_fields'
                     : parsed.hasOwnProperty('fields')        ? 'fields'
                     : hints.bodyKeys?.metrics || 'fields';
      if (!Array.isArray(parsed[fieldKey])) parsed[fieldKey] = [];
      if (!parsed[fieldKey].includes(key)) parsed[fieldKey].push(key);
      setCustomBody(JSON.stringify(parsed, null, 2));
      setCustomBodyErr('');
      toast.success(`Added "${key}" to ${fieldKey}`);
    } catch { toast('Fix the JSON error first before adding metrics', { icon: 'ℹ️' }); }
  }

  // ── Remove a key from groupBy/split_by/dimensions ────────────────────────────
  function removeDimension(key) {
    try {
      const parsed = JSON.parse(customBody);
      if (Array.isArray(parsed.dimensions)) parsed.dimensions = parsed.dimensions.filter(k => k !== key);
      if (Array.isArray(parsed.split_by))  parsed.split_by   = parsed.split_by.filter(k => k !== key);
      if (Array.isArray(parsed.groupBy))   parsed.groupBy    = parsed.groupBy.filter(k => k !== key);
      setCustomBody(JSON.stringify(parsed, null, 2));
      setCustomBodyErr('');
    } catch { toast('Fix the JSON error first', { icon: 'ℹ️' }); }
  }

  // ── Remove a key from fields/data_fields/metrics ─────────────────────────────
  function removeMetric(key) {
    try {
      const parsed = JSON.parse(customBody);
      if (Array.isArray(parsed.metrics))     parsed.metrics     = parsed.metrics.filter(k => k !== key);
      if (Array.isArray(parsed.data_fields)) parsed.data_fields = parsed.data_fields.filter(k => k !== key);
      if (Array.isArray(parsed.fields))      parsed.fields      = parsed.fields.filter(k => k !== key);
      setCustomBody(JSON.stringify(parsed, null, 2));
      setCustomBodyErr('');
    } catch { toast('Fix the JSON error first', { icon: 'ℹ️' }); }
  }

  // ── Filter helpers ────────────────────────────────────────────────────────────
  function addFilter(key, value = '', operator = null) {
    if (activeFilters.find(a => a.key === key)) return;
    // Default op picked from partner's vocabulary (first wire value) — shape-aware.
    const firstOp = hints.filterOperators?.[0] || '=';
    const defaultOp = operator ?? firstOp;
    const updated = [...activeFilters, { key, value, operator: defaultOp }];
    setActiveFilters(updated);
    applyFiltersToBody(updated);
  }

  function removeFilter(key) {
    const updated = activeFilters.filter(f => f.key !== key);
    setActiveFilters(updated);
    applyFiltersToBody(updated);
  }

  function updateFilter(key, field, val) {
    const updated = activeFilters.map(f => f.key === key ? { ...f, [field]: val } : f);
    setActiveFilters(updated);
    applyFiltersToBody(updated);
  }

  function applyFiltersToBody(filters) {
    try {
      const parsed = JSON.parse(customBody);

      // Dispatcher writes filters in the partner's wire shape:
      //   json-values-array (PAS):           parsed.filters[i]  = { key, values:[...], operator }
      //   json-name-value-match (Criteo):    parsed.filters[i]  = { name, value:[...], match }
      //   json-field-value-operator (ST):    parsed.filterBy[i] = { field, value:[...], operator }
      //   query-string-only (Bidmachine):    no filters in body — params live in URL
      const shape = hints.shape;

      // Remove all filter keys first to avoid stale remnants
      delete parsed.filters;
      delete parsed.filterBy;

      if (filters.length === 0) {
        setCustomBody(JSON.stringify(parsed, null, 2));
        setCustomBodyErr('');
        return;
      }

      if (supportsFilters(shape)) {
        const entries = buildFilters(shape, filters);
        if (shape === 'json-field-value-operator') {
          parsed.filterBy = entries;
        } else {
          parsed.filters = entries;
        }
      }

      setCustomBody(JSON.stringify(parsed, null, 2));
      setCustomBodyErr('');
    } catch { /* body has JSON error, skip */ }
  }

  // ── Helper: check if a key is already active in the request body ──────────────
  function isDimensionActive(key) {
    try {
      const p = JSON.parse(customBody);
      return (p.dimensions || p.split_by || p.groupBy || []).includes(key);
    } catch { return false; }
  }

  function isMetricActive(key) {
    try {
      const p = JSON.parse(customBody);
      return (p.metrics || p.data_fields || p.fields || []).includes(key);
    } catch { return false; }
  }

  // ── Saved presets ────────────────────────────────────────────────────────
  function presetSummary(p) {
    const b = p.body || {};
    const dims = (b.dimensions || b.split_by || b.groupBy || []).length;
    const mets = (b.metrics || b.data_fields || b.fields || []).length;
    const nf   = Array.isArray(p.filters) ? p.filters.length : 0;
    const dateLabel = p.date_preset === 'custom' && p.date_from
      ? `${p.date_from} → ${p.date_to || ''}`
      : (DATE_PRESET_LABELS[p.date_preset] || 'Last 7 days');
    return `${dims} dim${dims !== 1 ? 's' : ''} · ${mets} metric${mets !== 1 ? 's' : ''} · ${nf} filter${nf !== 1 ? 's' : ''} · ${dateLabel}`;
  }

  async function saveCurrentAsPreset() {
    if (!selectedId) return toast.error('Select a partner config first');
    if (customBodyErr) return toast.error('Fix the JSON error first');
    const name = presetName.trim();
    if (!name) return toast.error('Give the preset a name');
    let body;
    try { body = JSON.parse(customBody); } catch { return toast.error('Invalid JSON in request body'); }
    setSavingPreset(true);
    try {
      const { data } = await axios.post(`/api/partner-configs/${selectedId}/presets`, {
        name, body, filters: activeFilters, datePreset, dateFrom, dateTo,
      });
      setSavedPresets(prev => {
        const rest = prev.filter(p => p.id !== data.preset.id && p.name !== data.preset.name);
        return [...rest, data.preset].sort((a, b) => a.name.localeCompare(b.name));
      });
      setActivePresetId(data.preset.id);
      setNamingPreset(false); setPresetName('');
      toast.success(data.updated ? `Preset "${name}" updated` : `Preset "${name}" saved`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save preset');
    } finally { setSavingPreset(false); }
  }

  function applySavedPreset(p) {
    setCustomBody(JSON.stringify(p.body || {}, null, 2));
    setCustomBodyErr('');
    setActiveFilters(Array.isArray(p.filters) ? p.filters : []);
    if (p.date_preset && p.date_preset !== 'custom') {
      applyPreset(p.date_preset); // re-derives fresh relative dates
    } else {
      setDatePreset('custom');
      if (p.date_from) setDateFrom(p.date_from);
      if (p.date_to)   setDateTo(p.date_to);
    }
    setConfigReady(true);
    setResult(null); setError(''); setPage(0); setSortCol(''); setSortDir(1);
    setActivePresetId(p.id);   // highlight in the panel; panel stays open
    toast.success(`Preset "${p.name}" applied — ready to fetch`);
  }

  async function deleteSavedPreset(p, e) {
    e.stopPropagation();
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    try {
      await axios.delete(`/api/partner-configs/presets/${p.id}`);
      setSavedPresets(prev => prev.filter(x => x.id !== p.id));
      toast.success('Preset deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  }

  async function renamePreset(p) {
    const name = editName.trim();
    if (!name) return toast.error('Name cannot be empty');
    if (name === p.name) { setEditingPresetId(null); return; }
    try {
      const { data } = await axios.put(`/api/partner-configs/presets/${p.id}`, { name });
      setSavedPresets(prev => prev.map(x => x.id === p.id ? data.preset : x)
        .sort((a, b) => a.name.localeCompare(b.name)));
      setEditingPresetId(null);
      toast.success(`Renamed to "${name}"`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Rename failed');
    }
  }

  async function updatePresetFromCurrent(p, e) {
    e.stopPropagation();
    if (!configReady) return toast.error('Set up a fetch first — then update the preset with it');
    if (customBodyErr) return toast.error('Fix the JSON error first');
    let body;
    try { body = JSON.parse(customBody); } catch { return toast.error('Invalid JSON in request body'); }
    if (!window.confirm(`Replace "${p.name}" with the current dimensions, metrics, filters and date range?`)) return;
    try {
      const { data } = await axios.put(`/api/partner-configs/presets/${p.id}`, {
        body, filters: activeFilters, datePreset, dateFrom, dateTo,
      });
      setSavedPresets(prev => prev.map(x => x.id === p.id ? data.preset : x));
      setActivePresetId(p.id);
      toast.success(`Preset "${p.name}" updated with current setup`);
    } catch (e2) {
      toast.error(e2.response?.data?.error || 'Update failed');
    }
  }

  async function runFetch() {
    if (!selectedId) return toast.error('Select a partner config');
    if (customBodyErr) return toast.error('Fix the JSON error first');
    let body;
    try { body = JSON.parse(customBody); } catch { return toast.error('Invalid JSON in request body'); }

    // Create a cancellable request source
    const source = axios.CancelToken.source();
    setCancelToken(source);
    setFetching(true); setResult(null); setError(''); setPage(0); setFetchedAt(null);

    try {
      const { data } = await axios.post(`/api/partner-configs/${selectedId}/fetch-raw`, {
        date_from: dateFrom,
        date_to: dateTo,
        custom_body: body,
      }, {
        timeout: 120000,       // 2 minutes — generous for large date ranges
        cancelToken: source.token,
      });

      setFetchedAt(new Date()); // record when data arrived
      setResult(data);
      const vis = {};
      (data.columns || []).forEach(c => { vis[c] = true; });
      setColVisibility(vis);
      setSortCol(''); setSortDir(1);
      if (data.rowCount === 0) toast('No rows returned for this date range and filter combination', { icon: 'ℹ️', duration: 5000 });
      else {
        const timeStr = data.fetchMs >= 1000
          ? `${(data.fetchMs / 1000).toFixed(1)}s`
          : `${data.fetchMs}ms`;
        toast.success(`${data.rowCount.toLocaleString()} rows fetched in ${timeStr}`);
      }
    } catch (e) {
      if (axios.isCancel(e)) {
        toast('Fetch cancelled', { icon: 'ℹ️' });
        setError('');
      } else {
        const errData = e.response?.data;
        const baseMsg = errData?.error || e.message || 'Fetch failed';
        const hint = errData?.hint ? ` (${errData.hint})` : '';
        const rawInfo = errData?.rawBody ? `\n\nAPI response: ${errData.rawBody}` : '';
        setError(`${baseMsg}${hint}${rawInfo}`);
        toast.error(baseMsg + hint, { duration: 6000 });
      }
    } finally {
      setFetching(false);
      setCancelToken(null);
    }
  }

  function handleCancel() {
    if (cancelToken) {
      cancelToken.cancel('User cancelled');
    }
  }

  function exportCSV() {
    if (!result?.rows?.length) return;
    const visibleCols = result.columns.filter(c => colVisibility[c] !== false);
    const header = visibleCols.join(',');
    const dataRows = result.rows.map(r =>
      visibleCols.map(c => `"${String(r[c] ?? '').replace(/"/g, '""')}"`).join(',')
    );
    const csv = [header, ...dataRows].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `fetch-engine-${selectedCfg?.name || 'report'}-${dateFrom}-${dateTo}.csv`;
    a.click();
    toast.success('CSV downloaded');
  }

  function exportJSON() {
    if (!result?.rows?.length) return;
    const visibleCols = result.columns.filter(c => colVisibility[c] !== false);
    // Only export visible columns
    const filtered = result.rows.map(r =>
      Object.fromEntries(visibleCols.map(c => [c, r[c] ?? null]))
    );
    const json = JSON.stringify(filtered, null, 2);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
    a.download = `fetch-engine-${selectedCfg?.name || 'report'}-${dateFrom}-${dateTo}.json`;
    a.click();
    toast.success('JSON downloaded');
  }

  // Dimension keys from hints — used to sort columns and detect ID-only fields
  const hintDimKeys    = new Set(hints.dimensions.map(d => d.key));
  const hintIdCols     = new Set(
    hints.dimensions.filter(d => d.hint && d.hint.includes('ID')).map(d => d.key)
  );

  // Sort columns: dimensions first (in hints order), then metrics, then others
  const visibleCols = result
    ? result.columns
        .filter(c => colVisibility[c] !== false)
        .sort((a, b) => {
          const aIsDim = hintDimKeys.has(a);
          const bIsDim = hintDimKeys.has(b);
          if (aIsDim && !bIsDim) return -1;
          if (!aIsDim && bIsDim) return 1;
          if (aIsDim && bIsDim) {
            const ai = hints.dimensions.findIndex(d => d.key === a);
            const bi = hints.dimensions.findIndex(d => d.key === b);
            return ai - bi;
          }
          return 0;
        })
    : [];

  // Filter
  const filteredRows = (result?.rows || []).filter(row => {
    if (!filterText) return true;
    return Object.values(row).some(v => String(v).toLowerCase().includes(filterText.toLowerCase()));
  });

  // Sort
  const sortedRows = sortCol
    ? [...filteredRows].sort((a, b) => {
        const av = a[sortCol], bv = b[sortCol];
        if (av === undefined || av === null) return 1;
        if (bv === undefined || bv === null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sortDir;
        return String(av).localeCompare(String(bv)) * sortDir;
      })
    : filteredRows;

  const pagedRows = sortedRows.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
  const totalPages = Math.ceil(sortedRows.length / PER_PAGE);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => -d);
    else { setSortCol(col); setSortDir(1); }
  }

  // ── Totals row — computed from ALL filtered rows (not just current page) ─────
  // Average columns: anything with cpm, ecpm, ctr, rate, fill, viewability, render, win
  const AVG_COL_RE = /cpm|ecpm|ctr|rate|fill|viewab|render|win/i;
  const totalsRow = React.useMemo(() => {
    if (!result?.rows?.length) return null;
    const rows = filteredRows;
    if (!rows.length) return null;
    const totals = { _isTotal: true };
    result.columns.forEach(col => {
      const nums = rows.map(r => r[col]).filter(v => typeof v === 'number');
      if (!nums.length) { totals[col] = '—'; return; }
      if (AVG_COL_RE.test(col)) {
        totals[col] = nums.reduce((s, v) => s + v, 0) / nums.length; // average
      } else {
        totals[col] = nums.reduce((s, v) => s + v, 0); // sum
      }
    });
    return totals;
  }, [result, filteredRows]);

  if (configs.length === 0) {
    return (
      <div className="card" style={{ textAlign:'center', padding:'50px 20px' }}>
        <Database size={36} style={{ color:'var(--text-muted)', marginBottom:'14px' }}/>
        <div style={{ fontSize:'15px', fontWeight:700, color:'var(--text-primary)', marginBottom:'8px' }}>No Partner API Configs</div>
        <div style={{ fontSize:'13px', color:'var(--text-muted)', marginBottom:'20px', maxWidth:'400px', margin:'0 auto 20px', lineHeight:1.7 }}>
          Set up at least one Partner API Configuration in Account Settings before using the Fetch Engine.
        </div>
        <a href="/account" className="btn btn-primary btn-sm">Go to Account Settings</a>
      </div>
    );
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>

      {/* Config selector card — always visible */}
      <div className="card">
        <div className="card-title" style={{ marginBottom:'14px' }}><Database size={14}/> Fetch Engine — Pull data from your Partner APIs</div>
        <div style={{ display:'flex', gap:'10px', alignItems:'end', flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:'220px' }}>
            <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px', fontWeight:600 }}>Select Partner Config</label>
            <select className="select-field" value={selectedId} onChange={e => handleConfigChange(e.target.value)}>
              <option value="">— Choose a partner config —</option>
              {configs.map(c => (
                <option key={c.id} value={c.id}>{c.name} ({c.role})</option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary" onClick={handleGo} disabled={!selectedId} style={{ alignSelf:'end', padding:'9px 28px', fontWeight:700 }}>
            Go →
          </button>
          {selectedCfg?.docs_url && (
            <a href={selectedCfg.docs_url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary" style={{ alignSelf:'end', display:'inline-flex', alignItems:'center', gap:'5px', fontSize:'12px' }}>
              <ExternalLink size={11}/> Docs
            </a>
          )}
        </div>
        {!configReady && !selectedId && (
          <div style={{ marginTop:'12px', fontSize:'12px', color:'var(--text-muted)', lineHeight:1.6 }}>
            Select a partner API config above and click <strong>Go</strong> to configure your fetch request with dimensions, metrics, and date range.
          </div>
        )}
      </div>

      {/* Everything below only shown after user clicks Go */}
      {configReady && selectedCfg && (<>

      {/* Config meta + Date range + Fetch / Cancel button */}
      <div className="card">
        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', fontSize:'11px', color:'var(--text-muted)', marginBottom:'14px' }}>
          <span style={{ fontWeight:700, color:'var(--text-primary)', fontSize:'13px' }}>{selectedCfg.name}</span>
          <span style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'4px', padding:'2px 8px' }}>{selectedCfg.method}</span>
          <span style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'4px', padding:'2px 8px', fontFamily:'var(--font-mono)', maxWidth:'280px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selectedCfg.endpoint_url}</span>
          <span style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'4px', padding:'2px 8px', color: selectedCfg.has_token ? 'var(--green)' : 'var(--amber)' }}>
            {selectedCfg.has_token ? '🔑 Token saved' : '⚠ No token'}
          </span>
        </div>

        {/* Quick date presets */}
        <div style={{ marginBottom:'10px' }}>
          <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'6px', fontWeight:600 }}>Date Range</label>
          <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'8px' }}>
            {[
              { id:'today',     label:'Today' },
              { id:'yesterday', label:'Yesterday' },
              { id:'last3',     label:'Last 3 days' },
              { id:'last7',     label:'Last 7 days' },
              { id:'thisMonth', label:'This Month' },
              { id:'lastMonth', label:'Last Month' },
              { id:'custom',    label:'Custom' },
            ].map(p => (
              <button key={p.id}
                className="btn btn-sm"
                onClick={() => applyPreset(p.id)}
                style={{
                  fontSize:'11px', padding:'4px 12px',
                  background: datePreset === p.id ? 'color-mix(in srgb, var(--cyan) 15%, transparent)' : 'var(--bg-elevated)',
                  color: datePreset === p.id ? 'var(--cyan)' : 'var(--text-muted)',
                  border: `1px solid ${datePreset === p.id ? 'color-mix(in srgb, var(--cyan) 40%, transparent)' : 'var(--border)'}`,
                  fontWeight: datePreset === p.id ? 700 : 400,
                }}>
                {p.label}
              </button>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
            <input type="date" className="input-field" style={{ colorScheme:'dark' }} value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setDatePreset('custom'); }}/>
            <input type="date" className="input-field" style={{ colorScheme:'dark' }} value={dateTo}
              onChange={e => { setDateTo(e.target.value); setDatePreset('custom'); }}/>
          </div>
        </div>

        {/* Fetch + Cancel buttons */}
        <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
          <button className="btn btn-primary" onClick={runFetch} disabled={fetching || !selectedId}>
            {fetching
              ? <><RefreshCw size={12} style={{ animation:'spin 1s linear infinite' }}/> Fetching...</>
              : <><Play size={12}/> Fetch Data</>}
          </button>
          {fetching && (
            <button className="btn btn-secondary" onClick={handleCancel}
              style={{ color:'var(--red)', borderColor:'color-mix(in srgb, var(--red) 40%, transparent)', background:'color-mix(in srgb, var(--red) 8%, transparent)' }}>
              <X size={12}/> Cancel
            </button>
          )}
          {fetchedAt && datePreset === 'today' && !fetching && (() => {
            // Detect partner timezone from config notes or use UTC as default
            const tz = selectedCfg?.notes?.match(/UTC[+-]\d+(?::\d+)?|[A-Z][a-z]+\/[A-Z][a-z_]+/)?.[0] || 'UTC';
            const localTime = fetchedAt.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit', second:'2-digit' });
            const utcTime = fetchedAt.toUTCString().match(/\d{2}:\d{2}:\d{2}/)?.[0];
            return (
              <span style={{ fontSize:'11px', color:'var(--text-muted)' }}>
                ⏱ Data fetched up to{' '}
                <strong style={{ color:'var(--cyan)' }}>{localTime}</strong>
                {' '}(your local time)
                {utcTime && <span style={{ color:'var(--text-muted)', marginLeft:'4px' }}>· {utcTime} UTC</span>}
              </span>
            );
          })()}

          {/* Preset actions — right-aligned on the fetch row */}
          <div style={{ marginLeft:'auto', display:'flex', gap:'8px', alignItems:'center' }}>
            <button className="btn btn-secondary" disabled={!selectedId || !configReady}
              title="Save the current dimensions, metrics, filters and date range as a one-click preset"
              onClick={() => { setNamingPreset(v => !v); setShowPresets(false); setPresetName(''); }}>
              <BookmarkPlus size={12}/> Save as Preset
            </button>
            <button className="btn btn-secondary" disabled={!selectedId}
              style={ showPresets ? { color:'var(--cyan)', borderColor:'color-mix(in srgb, var(--cyan) 40%, transparent)' } : undefined }
              onClick={() => { setShowPresets(v => !v); setNamingPreset(false); }}>
              <Bookmark size={12}/> Saved Presets{savedPresets.length > 0 ? ` (${savedPresets.length})` : ''}
            </button>
          </div>
        </div>

        {/* Name + save the current setup */}
        {namingPreset && (
          <div style={{ display:'flex', gap:'8px', alignItems:'center', marginTop:'10px' }}>
            <input className="input-field" autoFocus maxLength={60}
              placeholder='Preset name — e.g. "Daily revenue by bundle"'
              value={presetName} onChange={e => setPresetName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') saveCurrentAsPreset();
                if (e.key === 'Escape') { setNamingPreset(false); setPresetName(''); }
              }}
              style={{ maxWidth:'340px', fontSize:'12px' }}/>
            <button className="btn btn-primary btn-sm" onClick={saveCurrentAsPreset} disabled={savingPreset}>
              {savingPreset ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setNamingPreset(false); setPresetName(''); }}>
              Cancel
            </button>
          </div>
        )}

        {/* Saved presets — click a row to apply */}
        {showPresets && (
          <div style={{ marginTop:'10px', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'10px' }}>
            {savedPresets.length === 0 ? (
              <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>
                No presets saved for this config yet. Set up your dimensions, metrics, filters and date range, then hit "Save as Preset".
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                {savedPresets.map(p => (
                  <div key={p.id} onClick={() => { if (editingPresetId !== p.id) applySavedPreset(p); }}
                    style={{ display:'flex', alignItems:'center', gap:'10px', padding:'8px 10px',
                      background: activePresetId === p.id ? 'color-mix(in srgb, var(--cyan) 6%, transparent)' : 'var(--bg-card)',
                      border: activePresetId === p.id ? '1px solid color-mix(in srgb, var(--cyan) 55%, transparent)' : '1px solid var(--border)',
                      borderRadius:'var(--radius-sm)',
                      cursor:'pointer', transition:'border-color 0.15s, background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--cyan) 55%, transparent)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = activePresetId === p.id ? 'color-mix(in srgb, var(--cyan) 55%, transparent)' : 'var(--border)'}>
                    <Play size={11} style={{ color:'var(--cyan)', flexShrink:0 }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      {editingPresetId === p.id ? (
                        <div style={{ display:'flex', gap:'6px', alignItems:'center' }} onClick={e => e.stopPropagation()}>
                          <input className="input-field" autoFocus maxLength={60} value={editName}
                            onChange={e => setEditName(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') renamePreset(p);
                              if (e.key === 'Escape') setEditingPresetId(null);
                            }}
                            style={{ fontSize:'12px', padding:'3px 8px', flex:1, minWidth:0 }}/>
                          <button className="btn btn-primary btn-sm" title="Save name"
                            onClick={e => { e.stopPropagation(); renamePreset(p); }}>
                            <Check size={11}/>
                          </button>
                          <button className="btn btn-secondary btn-sm" title="Cancel"
                            onClick={e => { e.stopPropagation(); setEditingPresetId(null); }}>
                            <X size={11}/>
                          </button>
                        </div>
                      ) : (
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', minWidth:0 }}>
                          <div style={{ fontSize:'12px', fontWeight:700, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {p.name}
                          </div>
                          {activePresetId === p.id && (
                            <span style={{ fontSize:'8px', fontWeight:800, letterSpacing:'0.8px', color:'var(--cyan)',
                              border:'1px solid color-mix(in srgb, var(--cyan) 50%, transparent)', borderRadius:'3px', padding:'1px 5px', flexShrink:0 }}>
                              ACTIVE
                            </span>
                          )}
                        </div>
                      )}
                      <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'1px' }}>
                        {presetSummary(p)}
                      </div>
                    </div>
                    <button onClick={e => { e.stopPropagation(); setEditingPresetId(p.id); setEditName(p.name); }} title="Rename preset"
                      style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', cursor:'pointer', color:'var(--text-muted)', padding:'5px 7px', flexShrink:0 }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--cyan)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}>
                      <Pencil size={11}/>
                    </button>
                    <button onClick={e => updatePresetFromCurrent(p, e)} title="Update this preset with the current setup"
                      style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', cursor:'pointer', color:'var(--text-muted)', padding:'5px 7px', flexShrink:0 }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--cyan)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}>
                      <RefreshCw size={11}/>
                    </button>
                    <button onClick={e => deleteSavedPreset(p, e)} title="Delete preset"
                      style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', cursor:'pointer', color:'var(--text-muted)', padding:'5px 7px', flexShrink:0 }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--red)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}>
                      <Trash2 size={11}/>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dimensions & Metrics hints + Request body + Filters — 2-col layout */}
      <div style={{ display:'grid', gridTemplateColumns:'280px 1fr', gap:'12px', alignItems:'start' }}>

        {/* Left: Dimension & Metric hints */}
        <div className="card" style={{ padding:'14px' }}>
          <div style={{ fontWeight:700, fontSize:'12px', color:'var(--text-primary)', marginBottom:'10px', display:'flex', alignItems:'center', gap:'6px' }}>
            <Filter size={12} style={{ color:'var(--cyan)' }}/> Dimensions & Metrics
          </div>
          <div style={{ fontSize:'10px', color:'var(--text-muted)', marginBottom:'8px', lineHeight:1.5 }}>
            Click any item to add it to your request body. Edit the request body JSON directly for full control.
          </div>

          <div style={{ marginBottom:'8px' }}>
            <div style={{ fontSize:'10px', fontWeight:700, color:'var(--cyan)', textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:'5px' }}>Dimensions / Group By</div>
            <div style={{ display:'flex', flexDirection:'column', gap:'3px' }}>
              {hints.dimensions.map(d => {
                const active = isDimensionActive(d.key);
                return (
                  <div key={d.key} style={{ display:'flex', flexDirection:'column', gap:'2px', padding:'4px 6px',
                    background: active ? 'color-mix(in srgb, var(--cyan) 8%, transparent)' : 'var(--bg-elevated)',
                    border: `1px solid ${active ? 'color-mix(in srgb, var(--cyan) 35%, transparent)' : 'var(--border)'}`,
                    borderRadius:'4px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                      <button onClick={() => insertDimension(d.key)} disabled={active}
                        style={{ flex:1, display:'flex', justifyContent:'space-between', alignItems:'center', background:'none', border:'none', cursor: active ? 'default' : 'pointer', textAlign:'left', gap:'6px', padding:0 }}>
                        <span style={{ fontSize:'11px', color: active ? 'var(--cyan)' : 'var(--text-primary)', fontWeight: active ? 700 : 500 }}>{d.label}</span>
                        <code style={{ fontSize:'9px', color:'var(--text-muted)', fontFamily:'var(--font-mono)' }}>{d.key}</code>
                      </button>
                      {active
                        ? <button onClick={() => removeDimension(d.key)} title="Remove" style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)', padding:'0 2px', fontSize:'12px', lineHeight:1, flexShrink:0 }}>✕</button>
                        : <button onClick={() => insertDimension(d.key)} title="Add" style={{ background:'none', border:'none', cursor:'pointer', color:'var(--cyan)', padding:'0 2px', fontSize:'14px', lineHeight:1, flexShrink:0 }}>+</button>
                      }
                    </div>
                    {d.hint && <div style={{ fontSize:'9px', color:'var(--amber)', lineHeight:1.4 }}>⚠ {d.hint}</div>}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div style={{ fontSize:'10px', fontWeight:700, color:'var(--purple)', textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:'5px' }}>Metrics / Data Fields</div>
            <div style={{ display:'flex', flexDirection:'column', gap:'3px' }}>
              {hints.metrics.map(m => {
                const active = isMetricActive(m.key);
                return (
                  <div key={m.key} style={{ display:'flex', flexDirection:'column', gap:'2px', padding:'4px 6px',
                    background: active ? 'color-mix(in srgb, var(--purple) 8%, transparent)' : 'var(--bg-elevated)',
                    border: `1px solid ${active ? 'color-mix(in srgb, var(--purple) 35%, transparent)' : 'var(--border)'}`,
                    borderRadius:'4px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                      <button onClick={() => insertMetric(m.key)} disabled={active}
                        style={{ flex:1, display:'flex', justifyContent:'space-between', alignItems:'center', background:'none', border:'none', cursor: active ? 'default' : 'pointer', textAlign:'left', gap:'6px', padding:0 }}>
                        <span style={{ fontSize:'11px', color: active ? 'var(--purple)' : 'var(--text-primary)', fontWeight: active ? 700 : 500 }}>{m.label}</span>
                        <code style={{ fontSize:'9px', color:'var(--text-muted)', fontFamily:'var(--font-mono)' }}>{m.key}</code>
                      </button>
                      {active
                        ? <button onClick={() => removeMetric(m.key)} title="Remove" style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)', padding:'0 2px', fontSize:'12px', lineHeight:1, flexShrink:0 }}>✕</button>
                        : <button onClick={() => insertMetric(m.key)} title="Add" style={{ background:'none', border:'none', cursor:'pointer', color:'var(--purple)', padding:'0 2px', fontSize:'14px', lineHeight:1, flexShrink:0 }}>+</button>
                      }
                    </div>
                    {m.note && <div style={{ fontSize:'9px', color:'var(--amber)', lineHeight:1.4 }}>⚠ {m.note}</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {selectedCfg?.docs_url && (
            <a href={selectedCfg.docs_url} target="_blank" rel="noopener noreferrer"
              style={{ display:'flex', alignItems:'center', gap:'5px', marginTop:'10px', fontSize:'11px', color:'var(--cyan)', textDecoration:'none' }}>
              <ExternalLink size={10}/> Full API Docs
            </a>
          )}
        </div>

        {/* Right column: Request body + Filters stacked */}
        <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>

          {/* Request body editor */}
          <div className="card" style={{ padding:'14px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
              <div style={{ fontWeight:700, fontSize:'12px', color:'var(--text-primary)', display:'flex', alignItems:'center', gap:'6px' }}>
                <FileText size={12} style={{ color:'var(--amber)' }}/> Request Body (JSON)
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => {
                const cfg = configs.find(c => String(c.id) === selectedId);
                if (cfg?.request_body) {
                  setCustomBody(JSON.stringify(cfg.request_body, null, 2));
                  setCustomBodyErr('');
                  toast('Reset to saved config body');
                }
              }}>↺ Reset</button>
            </div>
            <div style={{ fontSize:'10px', color:'var(--text-muted)', marginBottom:'6px', lineHeight:1.5 }}>
              Modify this to change what data is fetched. Use <code style={{ fontFamily:'var(--font-mono)' }}>{'{date_from}'}</code> and <code style={{ fontFamily:'var(--font-mono)' }}>{'{date_to}'}</code> as date placeholders. Auth headers are applied automatically from your saved config.
            </div>
            <textarea
              value={customBody}
              onChange={e => validateBody(e.target.value)}
              style={{ width:'100%', minHeight:'280px', fontFamily:'var(--font-mono)', fontSize:'12px', resize:'vertical', boxSizing:'border-box',
                background:'var(--bg-elevated)', border:`1px solid ${customBodyErr ? 'var(--red)' : 'var(--border)'}`,
                borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'10px', outline:'none', lineHeight:1.6 }}
            />
            {customBodyErr && <div style={{ fontSize:'10px', color:'var(--red)', marginTop:'3px' }}>✗ {customBodyErr}</div>}
          </div>

          {/* Filters — directly below request body in same column */}
          {hints.filters && hints.filters.length > 0 && (
            <div className="card" style={{ padding:'14px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'12px' }}>
                <Filter size={13} style={{ color:'var(--amber)' }}/>
                <span style={{ fontWeight:700, fontSize:'13px', color:'var(--text-primary)' }}>Filters</span>
                {activeFilters.length > 0 && (
                  <span className="badge badge-muted" style={{ fontSize:'10px' }}>{activeFilters.length} active</span>
                )}
                <span style={{ fontSize:'11px', color:'var(--text-muted)', marginLeft:'4px' }}>
                  — Click <strong style={{ color:'var(--amber)' }}>+</strong> to add a filter. Values auto-update the request body.
                </span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:'8px' }}>
                {hints.filters.map(f => {
                  const active = activeFilters.find(a => a.key === f.key);
                  return (
                    <div key={f.key} style={{ display:'flex', flexDirection:'column', gap:'6px', padding:'10px 12px',
                      background: active ? 'color-mix(in srgb, var(--amber) 7%, transparent)' : 'var(--bg-elevated)',
                      border: `1px solid ${active ? 'color-mix(in srgb, var(--amber) 40%, transparent)' : 'var(--border)'}`,
                      borderRadius:'var(--radius-sm)' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                        <span style={{ fontSize:'12px', fontWeight:700, color: active ? 'var(--amber)' : 'var(--text-primary)', flex:1 }}>
                          {f.label}
                        </span>
                        <code style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'var(--font-mono)' }}>{f.key}</code>
                        {active
                          ? <button onClick={() => removeFilter(f.key)} title="Remove filter"
                              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)', fontSize:'13px', lineHeight:1, flexShrink:0, padding:'0 2px' }}>✕</button>
                          : <button onClick={() => addFilter(f.key)} title="Add filter"
                              style={{ background:'color-mix(in srgb, var(--amber) 15%, transparent)', border:'1px solid color-mix(in srgb, var(--amber) 40%, transparent)', borderRadius:'4px', cursor:'pointer', color:'var(--amber)', fontSize:'13px', lineHeight:1, flexShrink:0, padding:'1px 7px', fontWeight:700 }}>+</button>
                        }
                      </div>
                      {active && (() => {
                        const shape = hints.shape;
                        const vals = active.value ? active.value.split(',').filter(v=>v.trim()).length : 0;
                        const isMulti = vals > 1;
                        const firstOp = hints.filterOperators?.[0] || '=';
                        const op = active.operator || firstOp;
                        // Per-field operators: if the dimension declares an `operators` list,
                        // narrow the dropdown to just those. Otherwise show all partner ops.
                        const dim = hints.dimensions.find(d => d.key === f.key);
                        const allowedWires = dim?.operators;
                        const opChoices = allowedWires
                          ? hints.filterOperators.filter(w => allowedWires.includes(w))
                          : hints.filterOperators;
                        const previewShape = shape === 'json-values-array'
                          ? '"values":[...],"operator":"IN"'
                          : shape === 'json-field-value-operator'
                          ? '"value":[...],"operator":"IN"'
                          : '"value":[...]';
                        return (
                          <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                            <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
                              {/* Always show operator dropdown — even with multiple values */}
                              <select style={{ fontSize:'11px', padding:'4px 6px', borderRadius:'4px', background:'var(--bg-card)',
                                  border:'1px solid var(--border)', color:'var(--text-primary)', flexShrink:0, width:'110px' }}
                                value={op} onChange={e => updateFilter(f.key, 'operator', e.target.value)}>
                                {opChoices.length > 0
                                  ? opChoices.map(w => <option key={w} value={w}>{w}</option>)
                                  : <option value={op}>{op}</option>
                                }
                              </select>
                              <input
                                style={{ fontSize:'11px', padding:'4px 8px', borderRadius:'4px', background:'var(--bg-card)',
                                  border:'1px solid var(--border)', color:'var(--text-primary)', flex:1, minWidth:0,
                                  fontFamily: 'var(--font-mono)' }}
                                placeholder={f.hint}
                                value={active.value}
                                onChange={e => updateFilter(f.key, 'value', e.target.value)}
                              />
                            </div>
                            {isMulti
                              ? <div style={{ fontSize:'10px', color:'var(--cyan)', lineHeight:1.4 }}>
                                  ✓ {vals} values →{' '}
                                  {(op === '=' || op === 'equals' || op === 'EQUALS')
                                    ? <code style={{ fontFamily:'var(--font-mono)', fontSize:'9px' }}>{previewShape}</code>
                                    : <><code style={{ fontFamily:'var(--font-mono)', fontSize:'9px' }}>{op}</code> applied to each value</>
                                  }
                                </div>
                              : <div style={{ fontSize:'10px', color:'var(--text-muted)', lineHeight:1.4 }}>
                                  Tip: comma-separate multiple values e.g. <code style={{ fontFamily:'var(--font-mono)' }}>627, 931</code>
                                </div>
                            }
                          </div>
                        );
                      })()}
                      {!active && (
                        <div style={{ fontSize:'10px', color:'var(--text-muted)', lineHeight:1.5 }}>{f.hint}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>{/* end right column */}
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-error" style={{ flexDirection:'column', alignItems:'flex-start', gap:'6px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'6px', fontWeight:700 }}>
            <AlertTriangle size={13}/> Fetch failed
          </div>
          <div style={{ fontSize:'12px', whiteSpace:'pre-wrap', wordBreak:'break-all' }}>{error}</div>
          <div style={{ fontSize:'11px', opacity:0.8, borderTop:'1px solid color-mix(in srgb, var(--red) 25%, transparent)', paddingTop:'6px', marginTop:'2px' }}>
            For assistance, reach out to IgnitorAds Support from the <a href="/support" style={{ color:'inherit', fontWeight:700, textDecoration:'underline' }}>Support section</a>.
          </div>
        </div>
      )}

      {/* Results */}
      {result && result.rowCount > 0 && (
        <div className="card">
          {/* Results header */}
          <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'12px', flexWrap:'wrap' }}>
            <div className="card-title" style={{ margin:0 }}>
              <BarChart2 size={14}/> Results
              <span className="badge badge-muted" style={{ marginLeft:'6px' }}>{result.rowCount.toLocaleString()} rows</span>
              <span style={{ fontSize:'10px', color: result.fetchMs > 10000 ? 'var(--amber)' : 'var(--text-muted)', fontWeight:400, marginLeft:'6px' }}>
                {result.fetchMs >= 1000
                  ? `${(result.fetchMs / 1000).toFixed(1)}s`
                  : `${result.fetchMs}ms`}
                {result.fetchMs > 10000 && <span style={{ marginLeft:'4px', fontSize:'9px' }}>⚠ slow partner API</span>}
              </span>
            </div>

            {/* Column visibility toggle */}
            <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', flex:1 }}>
              {result.columns.map(c => (
                <button key={c} onClick={() => setColVisibility(v => ({ ...v, [c]: !v[c] }))}
                  className="btn btn-sm"
                  style={{ fontSize:'10px', padding:'2px 7px',
                    background: colVisibility[c] !== false ? 'color-mix(in srgb, var(--cyan) 12%, transparent)' : 'var(--bg-elevated)',
                    color: colVisibility[c] !== false ? 'var(--cyan)' : 'var(--text-muted)',
                    border: `1px solid ${colVisibility[c] !== false ? 'color-mix(in srgb, var(--cyan) 30%, transparent)' : 'var(--border)'}` }}>
                  <Eye size={9} style={{ marginRight:'3px' }}/>{c}
                </button>
              ))}
            </div>

            {/* Filter + Export */}
            <input className="input-field" placeholder="Filter rows..." value={filterText}
              onChange={e => { setFilterText(e.target.value); setPage(0); }}
              style={{ width:'160px', fontSize:'12px' }}/>
            <div style={{ display:'flex', gap:'6px' }}>
              <button className="btn btn-secondary btn-sm" onClick={exportCSV}
                title="Download as CSV — smallest file size, opens in Excel">
                <Download size={11}/> CSV
              </button>
              <button className="btn btn-secondary btn-sm" onClick={exportJSON}
                title="Download as JSON — preserves data types">
                <Download size={11}/> JSON
              </button>
            </div>
          </div>

          {/* Summary stat cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:'8px', marginBottom:'12px' }}>
            {[
              { label:'Total Rows',    value: result.rowCount.toLocaleString(),      color:'var(--cyan)' },
              { label:'Filtered',      value: filteredRows.length.toLocaleString(),   color:'var(--purple)' },
              { label:'Columns',       value: result.columns.length,                  color:'var(--amber)' },
              { label:'Fetch Time',
                value: result.fetchMs >= 1000
                  ? `${(result.fetchMs / 1000).toFixed(1)}s`
                  : `${result.fetchMs}ms`,
                color: result.fetchMs > 10000 ? 'var(--amber)' : 'var(--green)' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ padding:'10px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', textAlign:'center' }}>
                <div style={{ fontSize:'18px', fontWeight:900, color, fontFamily:'var(--font-mono)' }}>{value}</div>
                <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'2px', textTransform:'uppercase', letterSpacing:'0.5px' }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Data table */}
          <div style={{ overflowX:'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  {visibleCols.map(c => (
                    <th key={c} onClick={() => toggleSort(c)}
                      style={{ whiteSpace:'nowrap', fontSize:'11px', cursor:'pointer', userSelect:'none',
                        color: sortCol === c ? 'var(--cyan)' : undefined,
                        background: sortCol === c ? 'color-mix(in srgb, var(--cyan) 6%, transparent)' : undefined }}>
                      {c}
                      {sortCol === c ? (sortDir === 1 ? ' ↑' : ' ↓') : ' ⇅'}
                    </th>
                  ))}
                </tr>
                {/* Totals row — pinned in thead so it stays above data on all pages */}
                {totalsRow && (
                  <tr style={{ background:'color-mix(in srgb, var(--cyan) 6%, transparent)', borderBottom:'2px solid color-mix(in srgb, var(--cyan) 25%, transparent)' }}>
                    {visibleCols.map((c, ci) => {
                      const v = totalsRow[c];
                      const isAvg = AVG_COL_RE.test(c);
                      const isId  = hintIdCols.has(c);
                      return (
                        <td key={c} style={{ fontSize:'11px', fontFamily:'var(--font-mono)', fontWeight:700,
                          color: ci === 0 ? 'var(--cyan)' : 'var(--text-primary)', whiteSpace:'nowrap',
                          padding:'6px 8px' }}>
                          {ci === 0 ? 'TOTAL' :
                            v === '—' ? '—' :
                            typeof v === 'number'
                              ? isId
                                ? String(v)
                                : isAvg
                                  ? <span title="Average">{v.toFixed(4)} <span style={{ fontSize:'9px', color:'var(--text-muted)' }}>avg</span></span>
                                  : Number.isInteger(v) ? v.toLocaleString() : v.toFixed(4)
                              : v}
                        </td>
                      );
                    })}
                  </tr>
                )}
              </thead>
              <tbody>
                {pagedRows.map((row, i) => (
                  <tr key={i}>
                    {visibleCols.map(c => {
                      const v = row[c];
                      const isId = hintIdCols.has(c);
                      return (
                        <td key={c} style={{ fontSize:'12px', fontFamily: typeof v === 'number' ? 'var(--font-mono)' : undefined, whiteSpace:'nowrap' }}>
                          {typeof v === 'number'
                            ? isId
                              ? String(v)  // ID columns — no comma formatting
                              : Number.isInteger(v) ? v.toLocaleString() : v.toFixed(4)
                            : String(v ?? '—')}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display:'flex', gap:'8px', alignItems:'center', justifyContent:'center', marginTop:'12px', fontSize:'12px', color:'var(--text-muted)' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.max(0, p-1))} disabled={page === 0}>← Prev</button>
              <span>Page {page+1} of {totalPages} ({sortedRows.length.toLocaleString()} rows)</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.min(totalPages-1, p+1))} disabled={page === totalPages-1}>Next →</button>
            </div>
          )}
        </div>
      )}

      {/* Raw response debug when 0 rows */}
      {result && result.rowCount === 0 && result.rawResponse && (
        <div className="card">
          <div className="alert alert-warning" style={{ marginBottom:'10px' }}>
            <AlertTriangle size={12}/> Zero rows returned — check your request body filters and date range.
          </div>
          <details>
            <summary style={{ cursor:'pointer', fontSize:'12px', color:'var(--text-muted)', fontWeight:600 }}>Raw API response (debug)</summary>
            <pre style={{ fontFamily:'var(--font-mono)', fontSize:'11px', color:'var(--text-secondary)', marginTop:'8px', whiteSpace:'pre-wrap', wordBreak:'break-all', background:'var(--bg-elevated)', padding:'10px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
              {result.rawResponse}
            </pre>
          </details>
        </div>
      )}

      </>)}
    </div>
  );
}


// ─── Standalone FetchEngine page ─────────────────────────────────────────────
export default function FetchEngine() {
  return (
    <div>
      <h1 className="page-title" style={{ display:'flex', alignItems:'center', gap:'10px' }}>
        Fetch Engine
        <span style={{ fontSize:'11px', fontWeight:800, background:'color-mix(in srgb, var(--purple) 20%, transparent)', color:'var(--purple)', border:'1px solid color-mix(in srgb, var(--purple) 40%, transparent)', borderRadius:'4px', padding:'2px 8px' }}>BETA</span>
      </h1>
      <p className="page-subtitle">Pull data directly from your Partner APIs and inspect, filter, sort and export the results.</p>
      <FetchEngineTab />
    </div>
  );
}
